// Staff game-events viewer - read-only inspection of a game's event log.

import { css, html } from 'lit';
import { api } from '../../lib/api.js';
import { BaseComponent } from '../../lib/base-component.js';
import { router } from '../../lib/router.js';
import '../shared/json-tree.js';

const PAGE_SIZE = 100;

class StaffGameEventsView extends BaseComponent {
  static properties = {
    gameId: { type: String, attribute: 'game-id' },
    _events: { type: Array, state: true },
    _expandedEventId: { type: String, state: true },
    _selectedViewByEventId: { type: Object, state: true },
    _hasMore: { type: Boolean, state: true },
    _loading: { type: Boolean, state: true },
    _loadingMore: { type: Boolean, state: true },
    _error: { type: String, state: true },
  };

  static styles = css`
    :host { display: block; }
    .chevron-toggle {
      transition: transform 200ms ease-in-out;
      display: inline-block;
    }
    .chevron-toggle.open { transform: rotate(-180deg); }
  `;

  constructor() {
    super();
    this.gameId = '';
    /** @type {Array<object>} */
    this._events = [];
    /** @type {string | null} */
    this._expandedEventId = null;
    /** @type {Record<string, string>} */
    this._selectedViewByEventId = {};
    this._hasMore = false;
    this._loading = true;
    this._loadingMore = false;
    this._error = null;
  }

  async connectedCallback() {
    await super.connectedCallback();
    await this._loadInitial();
  }

  async _loadInitial() {
    this._loading = true;
    this._error = null;
    try {
      const resp = await api.getStaffGameEvents(this.gameId, { limit: PAGE_SIZE });
      this._events = resp.events || [];
      this._hasMore = !!resp.hasMore;
    } catch (err) {
      this._error = /** @type {Error} */ (err).message || 'Failed to load events';
    } finally {
      this._loading = false;
    }
  }

  async _loadMore() {
    if (!this._hasMore || this._loadingMore) return;
    const last = this._events[this._events.length - 1];
    if (!last) return;
    this._loadingMore = true;
    try {
      const resp = await api.getStaffGameEvents(this.gameId, { since: last.eventId, limit: PAGE_SIZE });
      this._events = [...this._events, ...(resp.events || [])];
      this._hasMore = !!resp.hasMore;
    } catch (err) {
      this._error = /** @type {Error} */ (err).message || 'Failed to load more events';
    } finally {
      this._loadingMore = false;
    }
  }

  /** @param {object} event */
  _toggleRow(event) {
    if (this._expandedEventId === event.id) {
      this._expandedEventId = null;
      return;
    }
    this._expandedEventId = event.id;
    if (!this._selectedViewByEventId[event.id]) {
      this._selectedViewByEventId = {
        ...this._selectedViewByEventId,
        [event.id]: 'game',
      };
    }
  }

  /** @param {string} gameEventId @param {Event} e */
  _onViewChange(gameEventId, e) {
    const value = /** @type {HTMLSelectElement} */ (e.target).value;
    this._selectedViewByEventId = { ...this._selectedViewByEventId, [gameEventId]: value };
  }

  /**
   * Seconds from game start (the first event's createdAt in the loaded set).
   * Returns "0.0s" for the first event. Falls back to "-" if the reference
   * timestamp isn't available yet.
   * @param {object} event
   */
  _formatDelta(event) {
    const first = this._events[0];
    if (!first || !first.createdAt || !event.createdAt) return '-';
    const deltaMs = new Date(event.createdAt).getTime() - new Date(first.createdAt).getTime();
    return `${(deltaMs / 1000).toFixed(1)}s`;
  }

  /**
   * Rough byte size of an event's payload — JSON.stringify of the event
   * (game-event row plus nested player events). Not byte-for-byte identical
   * to what the API returns (Go marshaling whitespace, key ordering), but
   * within a few percent — enough for spotting outliers.
   * @param {object} event
   */
  _formatEventSize(event) {
    const bytes = JSON.stringify(event).length;
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
  }

  /** @param {object} event */
  _renderExpanded(event) {
    const hasPlayers = Array.isArray(event.playerEvents) && event.playerEvents.length > 0;
    const selectedView = this._selectedViewByEventId[event.id] || 'game';
    const selectedPlayer =
      hasPlayers && selectedView !== 'game'
        ? event.playerEvents.find((/** @type {object} */ p) => p.playerId === selectedView)
        : null;
    const changes = selectedPlayer ? selectedPlayer.changes : event.changes;

    return html`
      <div class="p-2">
        <div class="d-flex align-items-center mb-2 gap-2">
          <label class="form-label fw-semibold mb-0">View</label>
          <select
            class="form-select form-select-sm"
            style="max-width: 320px;"
            @change="${(/** @type {Event} */ e) => this._onViewChange(event.id, e)}"
            ?disabled="${!hasPlayers}"
          >
            <option value="game" ?selected="${selectedView === 'game'}">Game (raw)</option>
            ${
              hasPlayers
                ? event.playerEvents.map(
                    (/** @type {object} */ pe) => html`
                    <option value="${pe.playerId}" ?selected="${pe.playerId === selectedView}">
                      ${pe.playerName || pe.playerId}
                    </option>
                  `,
                  )
                : ''
            }
          </select>
        </div>

        ${
          selectedPlayer?.summary
            ? html`
              <div class="fw-semibold mb-1">Summary</div>
              <div class="mb-2">${selectedPlayer.summary}</div>
            `
            : ''
        }
        ${
          selectedPlayer?.banner
            ? html`
              <div class="fw-semibold mb-1">Banner</div>
              <div class="mb-2">
                <span class="fw-semibold">Title:</span> ${selectedPlayer.banner.title}
                <span class="ms-3 fw-semibold">Subtitle:</span> ${selectedPlayer.banner.subtitle || ''}
              </div>
            `
            : ''
        }
        ${
          selectedPlayer && Array.isArray(selectedPlayer.callouts) && selectedPlayer.callouts.length > 0
            ? html`
              <div class="fw-semibold mb-1">Callouts</div>
              <div class="bg-light border rounded p-2 mb-2">
                <json-tree .data="${selectedPlayer.callouts}" open-depth="2"></json-tree>
              </div>
            `
            : ''
        }

        <div class="fw-semibold mb-1">Changes</div>
        <div class="bg-light border rounded p-2 mb-0">
          <json-tree .data="${changes || []}" open-depth="2"></json-tree>
        </div>
      </div>
    `;
  }

  render() {
    return html`
      <nav aria-label="breadcrumb">
        <ol class="breadcrumb">
          <li class="breadcrumb-item">Staff</li>
          <li class="breadcrumb-item">
            <a
              href="/staff/games"
              @click="${(/** @type {Event} */ e) => {
                e.preventDefault();
                router.navigate('/staff/games');
              }}"
            >Games</a>
          </li>
          <li class="breadcrumb-item active" aria-current="page">Events</li>
        </ol>
      </nav>

      <h2>Game Events</h2>
      <div class="text-muted mb-3">
        Game ID: <span class="font-monospace small">${this.gameId}</span>
      </div>

      ${this._error ? html`<div class="alert alert-danger">${this._error}</div>` : ''}

      ${
        this._loading
          ? html`
            <div class="text-center py-4">
              <div class="spinner-border" role="status"><span class="visually-hidden">Loading…</span></div>
              <p class="mt-2">Loading events…</p>
            </div>
          `
          : this._events.length === 0
            ? html`<p class="text-muted">No events yet for this game.</p>`
            : html`
              <div class="table-responsive">
                <table class="table table-sm table-hover align-middle">
                  <thead>
                    <tr>
                      <th scope="col" style="width: 2rem;"></th>
                      <th scope="col" style="width: 5rem;">Event #</th>
                      <th scope="col">Type</th>
                      <th scope="col" style="width: 6rem;">Δt</th>
                      <th scope="col" style="width: 6rem;">Players</th>
                      <th scope="col" style="width: 7rem;">Event Size</th>
                    </tr>
                  </thead>
                  <tbody>
                    ${this._events.map((event) => {
                      const open = this._expandedEventId === event.id;
                      const playerCount = Array.isArray(event.playerEvents) ? event.playerEvents.length : 0;
                      return html`
                        <tr
                          role="button"
                          @click="${() => this._toggleRow(event)}"
                          aria-expanded="${open ? 'true' : 'false'}"
                        >
                          <td>
                            <i class="bi bi-chevron-down chevron-toggle ${open ? 'open' : ''}"></i>
                          </td>
                          <td class="font-monospace text-muted">${event.eventId}</td>
                          <td class="font-monospace fw-semibold">${event.eventType}</td>
                          <td class="text-muted small">${this._formatDelta(event)}</td>
                          <td>${playerCount > 0 ? playerCount : ''}</td>
                          <td class="text-muted small">${this._formatEventSize(event)}</td>
                        </tr>
                        ${
                          open
                            ? html`
                              <tr class="table-light">
                                <td></td>
                                <td colspan="5">${this._renderExpanded(event)}</td>
                              </tr>
                            `
                            : ''
                        }
                      `;
                    })}
                  </tbody>
                </table>
              </div>
              ${
                this._hasMore
                  ? html`
                    <div class="text-center my-3">
                      <button
                        class="btn btn-outline-secondary btn-sm"
                        ?disabled="${this._loadingMore}"
                        @click="${() => this._loadMore()}"
                      >
                        ${this._loadingMore ? 'Loading…' : 'Load more'}
                      </button>
                    </div>
                  `
                  : ''
              }
            `
      }
    `;
  }
}

customElements.define('staff-game-events-view', StaffGameEventsView);
